import { useAuthStore } from '@shared/stores/authStore';

/**
 * Permission gating for the logged-in member. An admin-capable member carries the
 * tenant role permissions (e.g. `support:request:read:all`, `members:requests:approve`)
 * on `user.permissions`. `admin:full:access` is the tenant wildcard and grants all.
 */
const WILDCARD = 'admin:full:access';

/** True when this permission set carries the wildcard or any one of `required`. */
export function permissionsAllowAny(permissions: string[], required: string[]): boolean {
  return permissions.includes(WILDCARD) || required.some((p) => permissions.includes(p));
}

export function getUserPermissions(): string[] {
  return useAuthStore.getState().user?.permissions ?? [];
}

export function hasPermission(permission: string): boolean {
  const perms = getUserPermissions();
  return perms.includes(WILDCARD) || perms.includes(permission);
}

export function hasAnyPermission(...permissions: string[]): boolean {
  return permissions.some(hasPermission);
}

/** Reactive hook — re-renders when the user's permissions change (login/switch). */
export function useHasPermission(permission: string): boolean {
  return useAuthStore((state) => {
    const perms = state.user?.permissions ?? [];
    return perms.includes(WILDCARD) || perms.includes(permission);
  });
}

export function useHasAnyPermission(...permissions: string[]): boolean {
  return useAuthStore((state) => permissionsAllowAny(state.user?.permissions ?? [], permissions));
}
